import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { message } from "antd";
import Layout from "../../Layout";
import HeaderBread from "../../components/HeaderBread";
import CustomHeaderTitle from "../../components/constants/CustomHeaderTitle";
import SwitchBar from "../../components/Pages/SwitchBar";
import SearchField from "../../components/SearchField";
import CustomizedTable from "../../components/UI/CustomizedTable";
import DeletePop from "../../constants/ErrorPermissionModal";
import Logs from "../../components/Logs";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import {
  deleteAgeValidation,
  getAgeValidation,
  updateAgeValidation,
} from "../../utils/ApiCalls/AgeValidation";
import { addlog } from "../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../utils/Constant/LogConstant";
import { checkPermission } from "../../utils/Validation/checkPermission";

const AgeValidation = () => {
  const navigate = useNavigate();
  const locationP = useLocation();
  const [{ userDetails, permission }, dispatch] = useDataLayerValue();

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Age Validation", route: "/shore/mobile-masters/age-validation" },
  ];

  const [active, setActive] = useState("listing");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);

  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(locationP.search).get("search") || ""
  );

  useEffect(() => {
    const params = new URLSearchParams();
    const currentParams = new URLSearchParams(locationP.search);

    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);

    if (currentParams.toString() !== params.toString()) {
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [page, pageSize, search, navigate, locationP.search]);

  const getData = async (value) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = { page: value, limit: pageSize };
    if (search) args.search = search;

    const data = await getAgeValidation(args);
    if (data.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
    } else {
      setRows(data.data || []);
      setCount(data.totalCount || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    if (active === "listing") getData(page);
  }, [active, page, pageSize, search]);

  const handleEdit = (id) => {
    if (checkPermission(permission, "update_per", "Age Validation", window?.location?.pathname)) {
      navigate("/shore/mobile-masters/age-validation/edit/" + id);
    } else {
      message.error("Update permission missing");
    }
  };

  const handleToggleStatus = async (id, checked) => {
    if (!checkPermission(permission, "update_per", "Age Validation", window?.location?.pathname)) {
      message.error("Update permission missing");
      return;
    }

    const title = rows.find((row) => row.id === id)?.name;

    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, isActive: checked ? 1 : 0 } : row))
    );

    const data = await updateAgeValidation(id, { isActive: checked ? 1 : 0 });
    if (data?.error) {
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: data.error, type: "error" },
      });
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, isActive: checked ? 0 : 1 } : row))
      );
    } else {
      addlog(
        `Age Validation ${title} ` + (checked ? LOG_ACTIONS.ACTIVATED : LOG_ACTIONS.DEACTIVATED),
        LOG_TYPES.AGE_VALIDATION,
        { id, title },
        userDetails.id
      );
    }
  };

  const handleDelete = async (id) => {
    if (checkPermission(permission, "delete_per", "Age Validation", window?.location?.pathname)) {
      const title = rows.find((row) => row.id === id)?.name;
      const data = await deleteAgeValidation(id);
      if (data?.error) {
        dispatch({
          type: "SET_ERROR_MESSAGE",
          errMess: { message: data.error, type: "error" },
        });
        return;
      }
      addlog(LOG_ACTIONS.REMOVED, LOG_TYPES.AGE_VALIDATION, { id, title }, userDetails.id);
      getData(page);
    } else {
      message.error("Missing Permission for delete");
    }
  };

  const columns = [
    {
      title: "SI",
      width: 30,
      dataIndex: "id",
      key: "id",
      render: (text, record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Age",
      dataIndex: "age",
      key: "age",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (value, record) => (
        <div className="custom-control custom-switch">
          <input
            type="checkbox"
            className="custom-control-input"
            id={`status-switch-${record.id}`}
            checked={!!value}
            onChange={(e) => handleToggleStatus(record.id, e.target.checked)}
          />
          <label
            className="custom-control-label"
            htmlFor={`status-switch-${record.id}`}
          ></label>
        </div>
      ),
    },
    {
      title: "Action",
      key: "operation",
      width: 40,
      render: (text, record) => (
        <div className="table_action_btn">
          <button className="btn" onClick={() => handleEdit(record.id)}>
            <i className="ri-edit-2-line" data-toggle="tooltip" title="Edit"></i>
          </button>
          <DeletePop onConfirm={() => handleDelete(record.id)}>
            <div>
              <button className="btn delete_btn">
                <i className="ri-delete-bin-line" data-toggle="tooltip" title="Delete"></i>
              </button>
            </div>
          </DeletePop>
        </div>
      ),
    },
  ];

  return (
    <Layout type={"read_per"} module_data={"Age Validation"}>
      <CustomHeaderTitle title={"Age Validation"} />
      <div>
        <div>
          <HeaderBread navigation={navigation} title={"Age Validation"}></HeaderBread>
        </div>
        <section className="content" style={{ marginTop: "20px" }}>
          <div className="container-fluid">
            <SwitchBar
              tabs={[
                { title: "Age Validation", value: "listing" },
                { value: "history", title: "History" },
              ]}
              activeTab={active}
              setActiveTab={setActive}
            />
            {active === "listing" && (
              <div className="card">
                <div className="card-header two_item">
                  <h5 className="card-title">Age Validation List</h5>
                  <div className="two_item">
                    <Link
                      to="/shore/mobile-masters/age-validation/add"
                      className="btn add_v_btn mx-2"
                    >
                      Add Age Validation
                    </Link>
                  </div>
                </div>
                <div className="card-body">
                  <div className="table_holder">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                      }}
                    >
                      <div style={{ maxWidth: 200 }}>
                        <SearchField
                          value={search}
                          SearchValue={() => setPage(1)}
                          setValue={(e) => setSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    <CustomizedTable
                      setRows={setRows}
                      setPageSize={setPageSize}
                      setPage={setPage}
                      pageSize={pageSize}
                      page={page}
                      count={count}
                      columns={columns}
                      rows={rows}
                      title={"Age Validation"}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        {active === "history" && (
          <Logs
            type={LOG_TYPES.AGE_VALIDATION}
            slug="/shore/mobile-masters/age-validation/edit/"
            removedClickable
          />
        )}
      </div>
    </Layout>
  );
};

export default AgeValidation;
